import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import presenciaApiService from '../services/PresenciaApiService';
import presenceClient from '../services/PresenceClient';
import LoadingSpinner from './common/LoadingSpinner';
import { isAdminColegio } from '../utils/adminMenu';
import './AdminColegioStats.css';
import './AdminUsuariosEnLinea.css';

const REALTIME_DEBOUNCE_MS = 400;
const PERIODIC_REFRESH_MS = 30000;
const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const ROLE_LABELS = {
  admin: 'Admin general',
  adminarea: 'Admin colegio',
  profesor: 'Profesor',
  alumno: 'Alumno',
  familiar: 'Familiar',
  profesorparticular: 'Profesor particular',
  alumnoparticular: 'Alumno particular',
  usuario: 'Usuario'
};

// Roles conocidos, para poder filtrar aunque en este momento no haya nadie conectado con ese rol.
const KNOWN_ROLES = ['Admin', 'AdminArea', 'Profesor', 'Alumno', 'Familiar', 'ProfesorParticular', 'AlumnoParticular'];

const TENANT_TYPE_LABELS = {
  colegios: 'Institución',
  particulares: 'Cuenta particular',
  global: 'Plataforma'
};

const formatRole = (value) => {
  const raw = (value || '').toString().trim();
  if (!raw) return '—';
  return ROLE_LABELS[raw.toLowerCase().replace(/[\s_-]/g, '')] || raw;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  if (seconds < 60) return 'menos de 1 min';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

const niceCeil = (value) => {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
};

const CHART = { width: 640, height: 200, left: 36, right: 14, top: 14, bottom: 26 };

const OnlineTimelineChart = ({ points }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const svgRef = useRef(null);

  const geometry = useMemo(() => {
    if (!Array.isArray(points) || points.length === 0) return null;
    const maxValue = niceCeil(Math.max(...points.map((point) => Number(point.usuarios || 0)), 1));
    const plotWidth = CHART.width - CHART.left - CHART.right;
    const plotHeight = CHART.height - CHART.top - CHART.bottom;
    const stepX = points.length > 1 ? plotWidth / (points.length - 1) : 0;
    const coords = points.map((point, index) => ({
      x: points.length > 1 ? CHART.left + index * stepX : CHART.left + plotWidth / 2,
      y: CHART.top + plotHeight - (Number(point.usuarios || 0) / maxValue) * plotHeight,
      point
    }));
    const line = coords.map((coord, index) => `${index === 0 ? 'M' : 'L'}${coord.x.toFixed(1)},${coord.y.toFixed(1)}`).join(' ');
    const baseY = CHART.top + plotHeight;
    const area = `${line} L${coords[coords.length - 1].x.toFixed(1)},${baseY} L${coords[0].x.toFixed(1)},${baseY} Z`;
    const ticks = [0, maxValue / 2, maxValue].map((value) => ({
      value,
      y: CHART.top + plotHeight - (value / maxValue) * plotHeight
    }));
    return { coords, line, area, ticks, baseY, plotWidth };
  }, [points]);

  if (!geometry) {
    return <div className="panel-empty">Todavía no hay historial. Se arma minuto a minuto desde que arrancó el servidor.</div>;
  }

  const handleMove = (event) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((event.clientX - rect.left) / rect.width) * CHART.width;
    let nearest = 0;
    let best = Infinity;
    geometry.coords.forEach((coord, index) => {
      const distance = Math.abs(coord.x - x);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });
    setHoverIndex(nearest);
  };

  const hovered = hoverIndex != null ? geometry.coords[hoverIndex] : null;
  const first = geometry.coords[0];
  const last = geometry.coords[geometry.coords.length - 1];
  const tooltipLeft = hovered ? `${(hovered.x / CHART.width) * 100}%` : '0%';

  return (
    <div className="online-chart">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        role="img"
        aria-label="Usuarios en línea por minuto durante la última hora"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {geometry.ticks.map((tick) => (
          <g key={tick.value}>
            <line
              className="online-chart-grid"
              x1={CHART.left}
              x2={CHART.width - CHART.right}
              y1={tick.y}
              y2={tick.y}
            />
            <text className="online-chart-axis" x={CHART.left - 8} y={tick.y + 4} textAnchor="end">
              {formatNumber(Math.round(tick.value))}
            </text>
          </g>
        ))}
        <path className="online-chart-area" d={geometry.area} />
        <path className="online-chart-line" d={geometry.line} />
        {geometry.coords.length === 1 && (
          <circle className="online-chart-dot" cx={first.x} cy={first.y} r="4" />
        )}
        {hovered && (
          <g>
            <line
              className="online-chart-crosshair"
              x1={hovered.x}
              x2={hovered.x}
              y1={CHART.top}
              y2={geometry.baseY}
            />
            <circle className="online-chart-dot" cx={hovered.x} cy={hovered.y} r="4.5" />
          </g>
        )}
        <text className="online-chart-axis" x={first.x} y={CHART.height - 6} textAnchor="start">
          {formatTime(first.point.fechaUtc)}
        </text>
        {geometry.coords.length > 1 && (
          <text className="online-chart-axis" x={last.x} y={CHART.height - 6} textAnchor="end">
            {formatTime(last.point.fechaUtc)}
          </text>
        )}
      </svg>
      {hovered && (
        <div
          className={`online-chart-tooltip${hovered.x > CHART.width * 0.7 ? ' is-left' : ''}`}
          style={{ left: tooltipLeft }}
        >
          <strong>{formatNumber(hovered.point.usuarios)} en línea</strong>
          <span>{formatTime(hovered.point.fechaUtc)}</span>
        </div>
      )}
    </div>
  );
};

const HorizontalBars = ({ items, emptyText }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="panel-empty">{emptyText}</div>;
  }

  const maxValue = Math.max(...items.map((item) => Number(item.total || 0)), 1);

  return (
    <ul className="online-bars">
      {items.map((item) => (
        <li key={item.key} title={`${item.label}: ${formatNumber(item.total)}`}>
          <div className="online-bars-label">
            <span>{item.label}</span>
            {item.hint && <small>{item.hint}</small>}
          </div>
          <div className="online-bars-track">
            <div
              className="online-bars-fill"
              style={{ width: `${Math.max(2, (Number(item.total || 0) / maxValue) * 100)}%` }}
            />
          </div>
          <strong className="online-bars-value">{formatNumber(item.total)}</strong>
        </li>
      ))}
    </ul>
  );
};

const AdminUsuariosEnLinea = () => {
  const navigate = useNavigate();
  const { user, logout, isLoading: authLoading } = useAuth();
  const [rolFilter, setRolFilter] = useState('todos');
  const [tipoTenant, setTipoTenant] = useState('todos');
  const [colegioId, setColegioId] = useState('');
  const [particularId, setParticularId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [liveStatus, setLiveStatus] = useState(presenceClient.getStatus());
  const requestIdRef = useRef(0);

  const isGeneralAdmin = useMemo(() => !isAdminColegio(user), [user]);
  const roleLabel = user?.roleName || user?.nombreRol || user?.role || 'Admin';

  const loadData = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++requestIdRef.current;
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
      setError('');
    }

    try {
      const response = await presenciaApiService.getOnlineUsers({
        rol: rolFilter,
        tipoTenant,
        colegioId: colegioId || undefined,
        profesorParticularId: particularId || undefined,
        search: search || undefined,
        page,
        pageSize
      });
      if (requestId !== requestIdRef.current) return;
      setData(response);
      setError('');
      if (response?.page && response.page !== page) {
        setPage(response.page);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      if (silent) {
        console.warn('No se pudieron refrescar los usuarios en línea', err);
      } else {
        setError(err.message || 'No pudimos cargar los usuarios en línea.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [rolFilter, tipoTenant, colegioId, particularId, search, page, pageSize]);

  const loadDataRef = useRef(loadData);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  // Carga inicial y recarga ante cualquier cambio de filtro o página.
  useEffect(() => {
    if (authLoading) return;
    if (!isGeneralAdmin) {
      setError('No tienes permisos para acceder a esta sección.');
      setIsLoading(false);
      return;
    }
    loadData({ silent: hasLoadedRef.current });
    hasLoadedRef.current = true;
  }, [authLoading, isGeneralAdmin, loadData]);

  // Tiempo real: el backend avisa cuando alguien se conecta o desconecta.
  useEffect(() => {
    if (authLoading || !isGeneralAdmin) return undefined;

    let debounceTimer = null;
    const unsubscribeChanges = presenceClient.subscribeToChanges(() => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadDataRef.current?.({ silent: true });
      }, REALTIME_DEBOUNCE_MS);
    });
    const unsubscribeStatus = presenceClient.subscribeToStatus(setLiveStatus);
    setLiveStatus(presenceClient.getStatus());

    // Refresco periódico: mantiene al día el historial por minuto y los tiempos en línea.
    const interval = setInterval(() => {
      loadDataRef.current?.({ silent: true });
    }, PERIODIC_REFRESH_MS);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      clearInterval(interval);
      unsubscribeChanges();
      unsubscribeStatus();
    };
  }, [authLoading, isGeneralAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== search) {
        setSearch(next);
        setPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const handleRolChange = (value) => {
    setRolFilter(value);
    setPage(1);
  };

  const handleTipoChange = (value) => {
    setTipoTenant(value);
    if (value !== 'colegios' && value !== 'todos') setColegioId('');
    if (value !== 'particulares' && value !== 'todos') setParticularId('');
    setPage(1);
  };

  const handleColegioChange = (value) => {
    setColegioId(value);
    if (value) {
      setParticularId('');
      setTipoTenant('colegios');
    }
    setPage(1);
  };

  const handleParticularChange = (value) => {
    setParticularId(value);
    if (value) {
      setColegioId('');
      setTipoTenant('particulares');
    }
    setPage(1);
  };

  const handleClearFilters = () => {
    setRolFilter('todos');
    setTipoTenant('todos');
    setColegioId('');
    setParticularId('');
    setSearchInput('');
    setPage(1);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const colegios = Array.isArray(data?.colegios) ? data.colegios : [];
  const particulares = Array.isArray(data?.particulares) ? data.particulares : [];
  const usuarios = Array.isArray(data?.usuarios) ? data.usuarios : [];
  const roleOptions = useMemo(() => {
    const seen = new Map();
    [...KNOWN_ROLES, ...(Array.isArray(data?.rolesDisponibles) ? data.rolesDisponibles : [])].forEach((value) => {
      const key = (value || '').toString().toLowerCase();
      if (key && !seen.has(key)) seen.set(key, value);
    });
    return Array.from(seen.values());
  }, [data]);

  const rolBars = (Array.isArray(data?.porRol) ? data.porRol : []).map((item) => ({
    key: item.rol,
    label: formatRole(item.rol),
    total: item.total
  }));
  const entidadBars = (Array.isArray(data?.topEntidades) ? data.topEntidades : []).map((item) => ({
    key: `${item.tipoTenant}-${item.id}`,
    label: item.nombre,
    hint: TENANT_TYPE_LABELS[item.tipoTenant] || '',
    total: item.total
  }));

  const hasActiveFilters =
    rolFilter !== 'todos' || tipoTenant !== 'todos' || !!colegioId || !!particularId || !!searchInput.trim();
  const totalFiltrados = Number(data?.totalFiltrados || 0);
  const currentPage = Number(data?.page || page);
  const totalPages = Number(data?.totalPages || 1);
  const rangeStart = totalFiltrados === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(totalFiltrados, currentPage * pageSize);
  const liveLabel =
    liveStatus === 'connected' ? 'En vivo' : liveStatus === 'reconnecting' ? 'Reconectando…' : 'Sin conexión en vivo';

  if (authLoading || (isLoading && !data)) {
    return (
      <div className="colegio-stats-page">
        <div className="colegio-stats-loading">
          <LoadingSpinner message="Cargando usuarios en línea..." />
        </div>
      </div>
    );
  }

  return (
    <div className="colegio-stats-page">
      <div className="colegio-stats-shell">
        <header className="colegio-stats-header">
          <div>
            <p className="eyebrow">Usuarios en línea</p>
            <h1>Presencia en tiempo real</h1>
            <p className="subtitle">
              Quién tiene la plataforma abierta en este momento. Se actualiza sola, sin recargar la página.
            </p>
            <p className={`online-live-status is-${liveStatus}`}>
              <span className="online-live-dot" aria-hidden="true" />
              <span>{liveLabel}</span>
              {data?.fechaUtc && <small>Última actualización {formatTime(data.fechaUtc)}</small>}
              {isRefreshing && <small>Actualizando…</small>}
            </p>
          </div>
          <div className="colegio-stats-header-actions">
            <div className="colegio-stats-user-chip">
              <span>{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <small>{roleLabel}</small>
            </div>
            <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left" />
              <span>Volver</span>
            </button>
            <button type="button" className="logout-btn-header" onClick={handleLogout} title="Cerrar sesión">
              <i className="fas fa-sign-out-alt" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </header>

        {error && (
          <div className="colegio-stats-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        {data && (
          <>
            <section className="colegio-stats-cards">
              <article className="colegio-stats-card">
                <p>Usuarios en línea</p>
                <strong>{formatNumber(data.totalUsuarios)}</strong>
                <span>En toda la plataforma</span>
              </article>
              <article className="colegio-stats-card">
                <p>Sesiones abiertas</p>
                <strong>{formatNumber(data.totalConexiones)}</strong>
                <span>Pestañas o dispositivos conectados</span>
              </article>
              <article className="colegio-stats-card">
                <p>Instituciones con actividad</p>
                <strong>{formatNumber(data.totalColegiosActivos)}</strong>
                <span>Con al menos un usuario en línea</span>
              </article>
              <article className="colegio-stats-card">
                <p>Cuentas particulares con actividad</p>
                <strong>{formatNumber(data.totalParticularesActivos)}</strong>
                <span>Con al menos un usuario en línea</span>
              </article>
              <article className="colegio-stats-card">
                <p>Pico de las últimas 24 h</p>
                <strong>{formatNumber(data.picoUsuarios)}</strong>
                <span>{data.picoFechaUtc ? `Registrado ${formatDateTime(data.picoFechaUtc)}` : 'Sin historial todavía'}</span>
              </article>
            </section>

            <section className="colegio-stats-panel">
              <div className="panel-header">
                <h2>Usuarios en línea, última hora</h2>
                <p>Total de la plataforma por minuto. No depende de los filtros.</p>
              </div>
              <OnlineTimelineChart points={Array.isArray(data.historial) ? data.historial : []} />
            </section>

            <section className="colegio-stats-toolbar">
              <div className="colegio-stats-custom-range">
                <label>
                  Rol
                  <select value={rolFilter} onChange={(e) => handleRolChange(e.target.value)}>
                    <option value="todos">Todos los roles</option>
                    {roleOptions.map((value) => (
                      <option key={value} value={value}>
                        {formatRole(value)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Tipo
                  <select value={tipoTenant} onChange={(e) => handleTipoChange(e.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="colegios">Instituciones</option>
                    <option value="particulares">Cuentas particulares</option>
                    <option value="global">Plataforma (sin institución)</option>
                  </select>
                </label>
                <label>
                  Institución
                  <select
                    value={colegioId}
                    onChange={(e) => handleColegioChange(e.target.value)}
                    disabled={tipoTenant === 'particulares' || tipoTenant === 'global'}
                  >
                    <option value="">Todas las instituciones</option>
                    {colegios.map((colegio) => (
                      <option key={colegio.id} value={colegio.id}>
                        {colegio.nombre}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Cuenta particular
                  <select
                    value={particularId}
                    onChange={(e) => handleParticularChange(e.target.value)}
                    disabled={tipoTenant === 'colegios' || tipoTenant === 'global'}
                  >
                    <option value="">Todas las cuentas</option>
                    {particulares.map((particular) => (
                      <option key={particular.id} value={particular.id}>
                        {particular.nombre}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Buscar
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Nombre o email"
                  />
                </label>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <i className="fas fa-eraser" />
                  <span>Limpiar filtros</span>
                </button>
              </div>
            </section>

            <section className="colegio-stats-grid">
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>En línea por rol</h2>
                  <p>{hasActiveFilters ? 'Según los filtros aplicados.' : 'Toda la plataforma.'}</p>
                </div>
                <HorizontalBars items={rolBars} emptyText="No hay usuarios en línea para mostrar." />
              </div>
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Instituciones y cuentas con más usuarios</h2>
                  <p>
                    {Number(data.totalEntidades || 0) > entidadBars.length
                      ? `Primeras ${entidadBars.length} de ${formatNumber(data.totalEntidades)}.`
                      : hasActiveFilters ? 'Según los filtros aplicados.' : 'Toda la plataforma.'}
                  </p>
                </div>
                <HorizontalBars items={entidadBars} emptyText="No hay instituciones ni cuentas con usuarios en línea." />
              </div>
            </section>

            <section className="colegio-stats-panel">
              <div className="panel-header">
                <h2>Usuarios conectados</h2>
                <p>
                  {totalFiltrados === 0
                    ? 'Nadie en línea para el filtro actual.'
                    : `Mostrando ${formatNumber(rangeStart)} a ${formatNumber(rangeEnd)} de ${formatNumber(totalFiltrados)}.`}
                </p>
              </div>
              {usuarios.length > 0 ? (
                <div className="panel-table-wrapper">
                  <table className="panel-table">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Tipo</th>
                        <th>Institución / cuenta</th>
                        <th>En línea desde</th>
                        <th>Tiempo en línea</th>
                        <th className="text-right">Sesiones</th>
                        <th>Dispositivo</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuarios.map((item) => (
                        <tr key={item.idUsuario}>
                          <td>
                            <div className="online-user-cell">
                              <span className="online-live-dot is-on" aria-hidden="true" />
                              <div>
                                <strong>{item.nombre || item.email || 'Sin nombre'}</strong>
                                {item.email && item.nombre && <small>{item.email}</small>}
                              </div>
                            </div>
                          </td>
                          <td>{formatRole(item.rol)}</td>
                          <td>{TENANT_TYPE_LABELS[item.tipoTenant] || '—'}</td>
                          <td>{item.entidadNombre || '—'}</td>
                          <td>{formatDateTime(item.conectadoDesdeUtc)}</td>
                          <td>{formatDuration(item.segundosEnLinea)}</td>
                          <td className="text-right">{formatNumber(item.conexiones)}</td>
                          <td>{item.dispositivo || '—'}</td>
                          <td>{item.ip || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="panel-empty">
                  {hasActiveFilters
                    ? 'No hay usuarios en línea que coincidan con los filtros.'
                    : 'No hay usuarios en línea en este momento.'}
                </div>
              )}

              <div className="online-pagination">
                <label>
                  Filas por página
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="online-pagination-nav">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setPage(1)}
                    disabled={currentPage <= 1}
                    title="Primera página"
                  >
                    <i className="fas fa-angle-double-left" />
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                  >
                    <i className="fas fa-angle-left" />
                    <span>Anterior</span>
                  </button>
                  <span className="online-pagination-info">
                    Página {formatNumber(currentPage)} de {formatNumber(totalPages)}
                  </span>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    <span>Siguiente</span>
                    <i className="fas fa-angle-right" />
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Última página"
                  >
                    <i className="fas fa-angle-double-right" />
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsuariosEnLinea;
